import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, X, FileText, AlertCircle, Trash2, Shield, Loader2 } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import './CampaignRemoveModal.css';

const CampaignRemoveModal = ({
  showModal,
  onClose,
  onUpload,
  onRemoveEntire,
  uploading = false,
  removingEntire = false,
  campaignName = '',
  source = ''
}) => {
  const { user } = useAuth();
  
  // Check admin status from both user object and localStorage (fallback)
  const getUserRole = () => {
    if (user?.role) {
      return user.role;
    }
    // Fallback to localStorage if user object is not available
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        return parsedUser?.role;
      }
    } catch (e) {
      console.error('Error parsing user from localStorage:', e);
    }
    return null;
  };
  
  const userRole = getUserRole();
  const isAdmin = userRole && (userRole.toLowerCase() === 'admin');
  
  // Debug: Log user info to console (remove in production)
  useEffect(() => {
    if (showModal) {
      console.log('CampaignRemoveModal - User object:', user);
      console.log('CampaignRemoveModal - User role from hook:', user?.role);
      console.log('CampaignRemoveModal - User role (with fallback):', userRole);
      console.log('CampaignRemoveModal - Is Admin:', isAdmin);
    }
  }, [showModal, user, userRole, isAdmin]);
  
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  const handleFileSelect = (file) => {
    setFileError('');
    
    // Validate file type
    if (!file.name.endsWith('.csv')) {
      setFileError('Please upload a CSV file');
      setSelectedFile(null);
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setFileError('File size must be less than 10MB');
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFileError('');
  };

  const handleCSVUpload = () => {
    if (!selectedFile) {
      setFileError('Please select a file first');
      return;
    }

    onUpload(selectedFile);
  };

  const handleConfirmDelete = () => {
    setShowConfirmDelete(true);
  };

  const handleDeleteConfirmed = () => {
    setShowConfirmDelete(false);
    onRemoveEntire();
  };

  const handleCancelDelete = () => {
    setShowConfirmDelete(false);
  };

  const handleClose = () => {
    setSelectedFile(null);
    setFileError('');
    setShowConfirmDelete(false);
    onClose();
  };

  return (
    <AnimatePresence>
      {showModal && (
        <>
          <motion.div
            className="campaign-remove-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
          />
          <motion.div
            className="campaign-remove-modal"
            initial={{ opacity: 0, scale: 0.9, x: '-50%', y: '-50%' }}
            animate={{ opacity: 1, scale: 1, x: '-50%', y: '-50%' }}
            exit={{ opacity: 0, scale: 0.9, x: '-50%', y: '-50%' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Full-modal loading overlay — shown while either delete is in flight */}
            {(removingEntire || uploading) && (
              <div className="campaign-remove-loading-overlay">
                <div className="campaign-remove-loading-content">
                  <Loader2 size={36} className="campaign-remove-spinner" />
                  <p className="campaign-remove-loading-title">
                    {removingEntire ? 'Removing campaign…' : 'Processing CSV removal…'}
                  </p>
                </div>
              </div>
            )}

            <div className="campaign-remove-modal-header">
              <h2 className="campaign-remove-modal-title">Remove Campaign from Customers</h2>
              <button className="campaign-remove-modal-close" onClick={handleClose}>×</button>
            </div>

            <div className="campaign-remove-modal-content">
              <div className="campaign-remove-info">
                <p className="campaign-info-text">
                  Campaign: <strong>{campaignName}</strong> ({source})
                </p>
              </div>

              {/* Remove Entire Campaign Section (Admin Only) */}
              {isAdmin && (
                <>
                  <div className="remove-option-section">
                    <div className="option-header">
                      <Shield size={20} className="option-icon admin-icon" />
                      <div className="option-header-text">
                        <h3 className="option-title">Remove Entire Campaign</h3>
                        <p className="option-description">
                          Permanently remove this campaign from all customers. This action cannot be undone.
                        </p>
                      </div>
                    </div>
                    {!showConfirmDelete ? (
                      <button
                        className="remove-entire-btn"
                        onClick={handleConfirmDelete}
                        disabled={removingEntire || uploading}
                      >
                        <Trash2 size={18} />
                        Remove Entire Campaign
                      </button>
                    ) : (
                      <div className="confirm-delete-section">
                        <div className="confirm-warning">
                          <AlertCircle size={20} />
                          <p>Are you sure you want to permanently remove this campaign from all customers?</p>
                        </div>
                        <div className="confirm-actions">
                          <button
                            className="confirm-cancel-btn"
                            onClick={handleCancelDelete}
                            disabled={removingEntire}
                          >
                            Cancel
                          </button>
                          <button
                            className="confirm-delete-btn"
                            onClick={handleDeleteConfirmed}
                            disabled={removingEntire}
                          >
                            {removingEntire ? (
                              <>
                                <div className="spinner-small"></div>
                                Removing...
                              </>
                            ) : (
                              <>
                                <Trash2 size={18} />
                                Confirm Delete
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="option-divider">
                    <span>OR</span>
                  </div>
                </>
              )}

              {/* Remove Campaign Using CSV Section */}
              <div className="remove-option-section">
                <div className="option-header">
                  <Upload size={20} className="option-icon" />
                  <div className="option-header-text">
                    <h3 className="option-title">Remove Campaign Using CSV</h3>
                    <p className="option-description">
                      Upload a CSV file containing customer emails or phone numbers. Only customers with matching emails or phone numbers will have this campaign removed.
                    </p>
                  </div>
                </div>

                <div
                  className={`file-upload-area ${dragActive ? 'drag-active' : ''} ${selectedFile ? 'has-file' : ''}`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  {selectedFile ? (
                    <div className="file-selected">
                      <FileText size={48} className="file-icon" />
                      <div className="file-details">
                        <p className="file-name">{selectedFile.name}</p>
                        <p className="file-size">
                          {(selectedFile.size / 1024).toFixed(2)} KB
                        </p>
                      </div>
                      <button
                        className="remove-file-btn"
                        onClick={handleRemoveFile}
                        disabled={uploading || removingEntire}
                      >
                        <X size={20} />
                      </button>
                    </div>
                  ) : (
                    <div className="file-upload-placeholder">
                      <Upload size={48} className="upload-icon" />
                      <p className="upload-text">
                        Drag and drop your CSV file here, or{' '}
                        <label className="upload-label">
                          browse
                          <input
                            type="file"
                            accept=".csv"
                            onChange={handleFileInputChange}
                            className="file-input"
                            disabled={uploading || removingEntire}
                          />
                        </label>
                      </p>
                      <p className="upload-hint">CSV file with customer emails or phone numbers</p>
                    </div>
                  )}
                </div>

                {fileError && (
                  <div className="file-error">
                    <AlertCircle size={16} />
                    <span>{fileError}</span>
                  </div>
                )}

                <button
                  className="remove-csv-btn"
                  onClick={handleCSVUpload}
                  disabled={!selectedFile || uploading || removingEntire}
                >
                  {uploading ? (
                    <>
                      <div className="spinner-small"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      <Upload size={18} />
                      Remove Campaign
                    </>
                  )}
                </button>
              </div>
            </div>

            <div className="campaign-remove-modal-actions">
              <button
                className="cancel-btn"
                onClick={handleClose}
                disabled={uploading || removingEntire}
              >
                Cancel
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CampaignRemoveModal;

